import {
  Button,
  Checkbox,
  FormControl,
  FormErrorIcon,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  UseDisclosureProps,
} from '@chakra-ui/react';
import { FC } from 'react';
import { useAppContext, useUpdateUser } from '@frx/frx-react-context';
import { useForm } from 'react-hook-form';
import { Modal, SelectField } from '../../../../components';
import { COUNTRIES, getDataTracking, STATES } from '../../../../utils';

export type EditInformationModalProps = Required<
  Pick<UseDisclosureProps, 'isOpen' | 'onClose'>
>;

export const EditInformationModal: FC<EditInformationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    data: {
      authData: { user },
    },
  } = useAppContext();
  const { mutate: updateUser, isLoading } = useUpdateUser();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const labelColor = 'brand.monochromatic.700';

  const onSubmit = (data: any) =>
    updateUser(
      { ...data },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xs">
      <Heading fontSize="24px">Edit your personal account information</Heading>
      <form
        aria-label="Update Account Information"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack py={8} spacing={6}>
          <FormControl isInvalid={!!errors.email?.message}>
            <FormLabel htmlFor="email" color={labelColor}>
              Email
            </FormLabel>
            <Input
              id="email"
              defaultValue={user?.email?.primary_address as string}
              {...register('email', {
                required: 'Email is required',
              })}
            />
            {!!errors?.email?.message && (
              <FormErrorMessage>
                <>
                  <FormErrorIcon />
                  {errors.email.message}
                </>
              </FormErrorMessage>
            )}
          </FormControl>
          <FormControl>
            <FormLabel color={labelColor}>Gender</FormLabel>
            <SelectField
              options={['Female', 'Male']}
              defaultValue={user?.gender as string}
              {...register('gender')}
            />
          </FormControl>
          <fieldset aria-label="Edit User Address">
            <legend>Address</legend>
            <Stack spacing={2}>
              <FormControl isInvalid={!!errors?.street1?.message}>
                <FormLabel
                  htmlFor="street1"
                  fontSize="10px"
                  color="brand.monochromatic.600"
                >
                  Mailing address. No PO boxes.
                </FormLabel>
                <Input
                  id="street1"
                  defaultValue={user?.primary_address?.street1 as string}
                  {...register('street1', {
                    required: 'Address is required',
                  })}
                />
                {!!errors?.street1?.message && (
                  <FormErrorMessage>
                    <>
                      <FormErrorIcon />
                      {errors.street1.message}
                    </>
                  </FormErrorMessage>
                )}
              </FormControl>
              <FormControl>
                <FormLabel
                  htmlFor="street2"
                  fontSize="10px"
                  color="brand.monochromatic.600"
                >
                  Address 2 (optional)
                </FormLabel>
                <Input
                  id="street2"
                  defaultValue={user?.primary_address?.street2 as string}
                  {...register('street2')}
                />
              </FormControl>
              <FormControl>
                <FormLabel
                  htmlFor="street3"
                  fontSize="10px"
                  color="brand.monochromatic.600"
                >
                  Address 3 (optional)
                </FormLabel>
                <Input
                  id="street3"
                  defaultValue={user?.primary_address?.street3 as string}
                  {...register('street3')}
                />
              </FormControl>
            </Stack>
          </fieldset>
          <FormControl isInvalid={!!errors?.city?.message}>
            <FormLabel htmlFor="city" color={labelColor}>
              City
            </FormLabel>
            <Input
              id="city"
              defaultValue={user?.primary_address?.city as string}
              {...register('city', {
                required: 'City is required',
              })}
            />
            {!!errors?.city?.message && (
              <FormErrorMessage>
                <>
                  <FormErrorIcon />
                  {errors.city.message}
                </>
              </FormErrorMessage>
            )}
          </FormControl>
          <FormControl isInvalid={!!errors?.state?.message}>
            <FormLabel color={labelColor}>State</FormLabel>
            <SelectField
              options={STATES}
              defaultValue={user?.primary_address?.state as string}
              {...register('state', {
                required: 'State is required',
              })}
            />
            {!!errors?.state?.message && (
              <FormErrorMessage>
                <>
                  <FormErrorIcon />
                  {errors.state.message}
                </>
              </FormErrorMessage>
            )}
          </FormControl>
          <FormControl isInvalid={!!errors?.zip?.message}>
            <FormLabel htmlFor="zip" color={labelColor}>
              Postal Code
            </FormLabel>
            <Input
              id="zip"
              defaultValue={user?.primary_address?.zip as string}
              {...register('zip', {
                required: 'Postal code is required',
              })}
            />
            {!!errors?.zip?.message && (
              <FormErrorMessage>
                <>
                  <FormErrorIcon />
                  {errors.zip.message}
                </>
              </FormErrorMessage>
            )}
          </FormControl>
          <FormControl>
            <FormLabel color={labelColor}>Country</FormLabel>
            <SelectField
              options={COUNTRIES}
              defaultValue={user?.primary_address?.country as string}
              {...register('country')}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="homePhone" color={labelColor}>
              Phone (optional)
            </FormLabel>
            <Input
              id="homePhone"
              defaultValue={user?.home_phone || ''}
              {...register('homePhone')}
            />
          </FormControl>
          <FormControl>
            <Checkbox
              id="homePhone"
              defaultChecked={Boolean(user?.email?.accepts_email)}
              {...register('acceptEmail')}
            >
              <Text fontSize="12px" color={labelColor}>
                I want to receive electronic messages from St. Jude Children’s
                Research Hospital that contain event updates and news about
                opportunities to support St. Jude
              </Text>
            </Checkbox>
          </FormControl>
        </Stack>
        <Button
          colorScheme="blue"
          w="full"
          type="submit"
          isLoading={isLoading}
          {...getDataTracking('editInfoSaveChangesButton')}
        >
          Save Changes
        </Button>
      </form>
    </Modal>
  );
};
